import { useEffect, useState } from 'react'
import { Phone, X, Loader, CheckCircle, AlertTriangle, AlertCircle, Minus } from 'lucide-react'
import { getServicesManager } from 'services';
import { OptionalTwilioNumber, RegisteredTwilioNumber } from 'interfaces/db';
import { MultiDialerQuotaInfo } from "interfaces/subscriptions";
import { updateUser, convertUserDataResultToReduxSafe } from 'lib/redux/users/slice';
import { useDispatch } from 'react-redux';
import { useUserData } from '../../lib/redux/users/user-data-hook';
import { sleep } from 'core';
import { NumberSearch } from './NumberSearch';
import { format_number } from './cfg';
import { usePurchasing } from './hooks';
import { formatTimeAgo } from './utils';
import { Typography } from 'interfaces/typography';

export function NumberManagementIndividual() {
  const { user } = useUserData();
  const dispatch = useDispatch();
  const [maxSelections, setMaxSelections] = useState(5)
  const [selectedNumbers, setSelectedNumbers] = useState<RegisteredTwilioNumber[]>([])
  const [confirmingReleaseNumber, setConfirmingReleaseNumber] = useState<string | null>(null)
  const [releaseNumberProgress, setReleaseNumberProgress] = useState<string | null>(null)
  const [multiDialerQuota, setMultiDialerQuota] = useState<MultiDialerQuotaInfo | null>(null)
  const [isEditingForwarding, setIsEditingForwarding] = useState(false)
  const [forwardingValue, setForwardingValue] = useState(user?.forwarding_number || '')
  const [isSavingForwarding, setIsSavingForwarding] = useState(false)

  const { purchasingNumbers, purchaseNumber } = usePurchasing(selectedNumbers, setSelectedNumbers)
  

  // on component mount
  useEffect(() => {
    getServicesManager().getMyTwilioNumbers().then((numbers) => {
      setSelectedNumbers(numbers ?? [])
    })

    getServicesManager().getMultidialerQuota().then((quota) => {
      setMultiDialerQuota(quota)
      if (quota) setMaxSelections(quota.subscription.max_phone_numbers_per_user)
    })
  }, [])

  if (!user) return <Loader />
  if (!multiDialerQuota) return <Loader />

  const handlePurchase = async (twilio_number: OptionalTwilioNumber, countryCode: string, numberType: 'Local' | 'Mobile' | 'TollFree') => {
    if (!user) return
    if (selectedNumbers.length >= maxSelections) {
      alert(`You can only select up to ${maxSelections} numbers.`)
      return
    }
    await purchaseNumber(twilio_number, user, countryCode, numberType)
  }

  const handleForwardingEdit = () => {
    setForwardingValue(user?.forwarding_number || '');
    setIsEditingForwarding(true);
  };

  const handleForwardingCancel = () => {
    setForwardingValue(user?.forwarding_number || '');
    setIsEditingForwarding(false);
  };

  const handleForwardingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForwardingValue(value);
  };

  const handleForwardingBlur = () => {
    let value = forwardingValue.trim();
    if (value && !value.startsWith('+')) {
      value = formatPhoneNumber(value);
      setForwardingValue(value);
    }
  };

  const handleForwardingSave = async () => {
    setIsSavingForwarding(true);
    let trimmedValue = forwardingValue.trim();
    
    // Format the number if it doesn't start with + before saving
    if (trimmedValue && !trimmedValue.startsWith('+')) {
      trimmedValue = formatPhoneNumber(trimmedValue);
      setForwardingValue(trimmedValue);
    }
    
    const numberToSave = trimmedValue || null;
    
    try {
      const result = await getServicesManager().updateForwardingNumber(numberToSave);
      if (result !== null && user) {
        // Update Redux store with new forwarding number
        const updatedUser = { ...user, forwarding_number: numberToSave };
        dispatch(updateUser(convertUserDataResultToReduxSafe(updatedUser)));
        setIsEditingForwarding(false);
      }
    } catch (error) {
      console.error('Failed to save forwarding number:', error);
    } finally {
      setIsSavingForwarding(false);
    }
  };

  const handleForwardingClear = async () => {
    setIsSavingForwarding(true);
    
    try {
      const result = await getServicesManager().updateForwardingNumber(null);
      if (result !== null && user) {
        // Update Redux store to clear forwarding number
        const updatedUser = { ...user, forwarding_number: null };
        dispatch(updateUser(convertUserDataResultToReduxSafe(updatedUser)));
        setForwardingValue('');
        setIsEditingForwarding(false);
      }
    } catch (error) {
      console.error('Failed to clear forwarding number:', error);
    } finally {
      setIsSavingForwarding(false);
    }
  };

  const formatPhoneNumber = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 10) {
      return `+1${cleaned}`;
    }
    return `+${cleaned}`;
  };

  function getSpamStatus(number: RegisteredTwilioNumber): 'clean' | 'flagged' | 'warning' | 'untested' {
    // Check if we have any test results at all
    const hasNomoroboResult = number.nomorobo_spamscore !== null;
    const hasIpQualityResult = number.ipqualityscore_score !== null;
    
    // If we have no results from either provider, show untested
    if (!hasNomoroboResult && !hasIpQualityResult) {
      return 'untested';
    }
    
    // Check if either provider flagged spam
    const nomoroboFlagged = hasNomoroboResult && number.nomorobo_spamscore && number.nomorobo_spamscore > 0.5;
    const ipQualityScoreFlagged = hasIpQualityResult && number.ipqualityscore_score !== null && number.ipqualityscore_score > 85;
    
    // If either provider shows spam, flag it
    if (nomoroboFlagged || ipQualityScoreFlagged) {
      return 'flagged';
    }
    
    // Check for warning status: Nomorobo clean but IPQuality 75-85
    const nomoroboClean = hasNomoroboResult && (!number.nomorobo_spamscore || number.nomorobo_spamscore <= 0.5);
    const ipQualityWarning = hasIpQualityResult && number.ipqualityscore_score !== null && number.ipqualityscore_score > 75 && number.ipqualityscore_score <= 85;
    
    if (nomoroboClean && ipQualityWarning) {
      return 'warning';
    }
    
    // If we have results but no spam detected, show clean
    return 'clean';
  }

  function getLatestSpamTestDate(number: RegisteredTwilioNumber): Date | null {
    const dates: Date[] = [];
    if (number.nomorobo_spamscore_as_of) {
      dates.push(new Date(number.nomorobo_spamscore_as_of));
    }
    if (number.ipqualityscore_as_of) {
      dates.push(new Date(number.ipqualityscore_as_of));
    }
    
    if (dates.length === 0) return null;
    
    return new Date(Math.max(...dates.map(date => date.getTime())));
  }

  return (
    <div className="container mx-auto p-4 bg-gray-50 min-h-screen">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div className="shadow-sm bg-white border-gray-200 border rounded-lg p-6">
          <Typography variant="h3" fontWeight={600} className="mb-4 text-gray-700 flex items-center justify-between">
            <span className="flex items-center">
              <Phone className="mr-2" size={20} />
              Your Numbers
            </span>
            <Typography variant="smallParagraph" fontSize="14px" color="#6b7280" fontWeight={400}>
              {selectedNumbers.length}/{maxSelections} Selected
            </Typography>
          </Typography>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-2 text-left">
                    <Typography variant="smallParagraph" fontSize="12px" fontWeight={600}>Number</Typography>
                  </th>
                  <th className="p-2 text-left">
                    <Typography variant="smallParagraph" fontSize="12px" fontWeight={600}>Spam</Typography>
                  </th>
                  <th className="p-2 text-left">
                    <Typography variant="smallParagraph" fontSize="12px" fontWeight={600}>Last Test</Typography>
                  </th>
                  <th className="p-2 text-left"></th>
                </tr>
              </thead>
              <tbody>
                {selectedNumbers.map((number) => {
                  const spamStatus = getSpamStatus(number);
                  const lastSpamTest = getLatestSpamTestDate(number);
                  return (
                    <tr key={number.rep_phone_value} className="border-b">
                      <td className="p-2">
                        <Typography variant="smallParagraph" fontSize="12px">{format_number(number.rep_phone_value)}</Typography>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          {spamStatus === 'flagged' ? (
                            <AlertTriangle className="text-red-500" size={16} aria-label="High Spam Score" />
                          ) : spamStatus === 'warning' ? (
                            <AlertCircle className="text-yellow-500" size={16} aria-label="Warning Spam Score" />
                          ) : spamStatus === 'clean' ? (
                            <CheckCircle className="text-green-500" size={16} aria-label="Low Spam Score" />
                          ) : (
                            <Minus className="text-gray-400" size={16} aria-label="Not Tested" />
                          )}
                        </div>
                      </td>
                      <td className="p-2">
                        {lastSpamTest ? (
                          <Typography variant="smallParagraph" fontSize="11px" color="#6b7280" fontWeight={400}>
                            {formatTimeAgo(lastSpamTest)}
                          </Typography>
                        ) : (
                          <Typography variant="smallParagraph" fontSize="11px" color="#6b7280" fontWeight={400}>
                            Never
                          </Typography>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <X className="text-red-500 inline-block cursor-pointer" size={16}
                           onClick={() => { setReleaseNumberProgress(null); setConfirmingReleaseNumber(number.rep_phone_value); }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Spam Status Guide */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-4">
              <Typography variant="smallParagraph" fontSize="11px" color="#374151" fontWeight={600}>
                Spam Status:
              </Typography>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <CheckCircle className="text-green-500" size={14} />
                  <Typography variant="smallParagraph" fontSize="10px" color="#374151" fontWeight={400}>Clean</Typography>
                </div>
                <div className="flex items-center gap-1">
                  <AlertCircle className="text-yellow-500" size={14} />
                  <Typography variant="smallParagraph" fontSize="10px" color="#374151" fontWeight={400}>Warning</Typography>
                </div>
                <div className="flex items-center gap-1">
                  <AlertTriangle className="text-red-500" size={14} />
                  <Typography variant="smallParagraph" fontSize="10px" color="#374151" fontWeight={400}>Flagged</Typography>
                </div>
                <div className="flex items-center gap-1">
                  <Minus className="text-gray-400" size={14} />
                  <Typography variant="smallParagraph" fontSize="10px" color="#374151" fontWeight={400}>Untested</Typography>
                </div>
              </div>
              <Typography variant="smallParagraph" fontSize="10px" color="#6b7280" fontWeight={400}>
                Tests run ~weekly via Nomorobo & IPQualityScore
              </Typography>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <Typography variant="h3" fontWeight={600} color="#111827">Call Forwarding</Typography>
                <Typography variant="smallParagraph" fontSize="13px" color="#6b7280">Forward calls when unavailable</Typography>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {isEditingForwarding ? (
              <div className="space-y-4">
                <div>
                  <Typography variant="smallParagraph" fontSize="13px" color="#374151" fontWeight={500} className="block mb-2">
                    Forwarding Number
                  </Typography>
                  <input
                    type="text"
                    value={forwardingValue}
                    onChange={handleForwardingChange}
                    onBlur={handleForwardingBlur}
                    placeholder="Enter phone number (e.g., +1234567890)"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    disabled={isSavingForwarding}
                    autoFocus
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleForwardingSave}
                    disabled={isSavingForwarding}
                    className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed font-medium text-sm"
                  >
                    {isSavingForwarding ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <Typography variant="smallParagraph" fontSize="14px" color="#ffffff">Saving...</Typography>
                      </div>
                    ) : (
                      <Typography variant="smallParagraph" fontSize="14px" color="#ffffff" fontWeight={500}>Save Changes</Typography>
                    )}
                  </button>
                  {user?.forwarding_number && (
                    <button
                      onClick={handleForwardingClear}
                      disabled={isSavingForwarding}
                      className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed font-medium text-sm"
                    >
                      <Typography variant="smallParagraph" fontSize="14px" color="#ffffff" fontWeight={500}>
                        {isSavingForwarding ? 'Clearing...' : 'Clear'}
                      </Typography>
                    </button>
                  )}
                  <button
                    onClick={handleForwardingCancel}
                    disabled={isSavingForwarding}
                    className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                  >
                    <Typography variant="smallParagraph" fontSize="14px" color="#374151" fontWeight={500}>Cancel</Typography>
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div 
                  onClick={handleForwardingEdit}
                  className="group cursor-pointer p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 hover:border-gray-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <Typography variant="smallCaption" fontSize="11px" color="#6b7280" fontWeight={500} className="mb-1">
                        Current setting
                      </Typography>
                      {user?.forwarding_number ? (
                        <Typography variant="h3" fontWeight={500} color="#111827">
                          {user.forwarding_number}
                        </Typography>
                      ) : (
                        <Typography variant="h3" color="#9ca3af" fontWeight={400}>
                          No forwarding number set
                        </Typography>
                      )}
                    </div>
                    <div className="flex items-center text-gray-400 group-hover:text-gray-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                  </div>
                  <Typography variant="smallParagraph" fontSize="13px" color="#6b7280" className="mt-2">
                    Click to {user?.forwarding_number ? 'edit' : 'set'} forwarding number
                  </Typography>
                </div>
                
                {user?.forwarding_number ? (
                  <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="w-5 h-5 text-blue-600 mt-0.5">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <Typography variant="smallParagraph" fontSize="13px" color="#1e40af" fontWeight={500}>Calls go to your phone</Typography>
                      <Typography variant="smallParagraph" fontSize="13px" color="#1d4ed8" className="mt-1">
                        Incoming calls will be forwarded to {user.forwarding_number}.
                      </Typography>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="w-5 h-5 text-green-600 mt-0.5">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <Typography variant="smallParagraph" fontSize="13px" color="#059669" fontWeight={500}>Calls go to your CRM</Typography>
                      <Typography variant="smallParagraph" fontSize="13px" color="#047857" className="mt-1">
                        Since no forwarding number is set, incoming calls will appear directly in your CRM/SEP interface.
                      </Typography>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {confirmingReleaseNumber === null ? null :
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <Typography variant="h3" fontWeight={600} className="mb-4 text-gray-700 flex items-center">
              Unregister {format_number(confirmingReleaseNumber)}?
            </Typography>
            <Typography variant="mediumParagraph" className="my-4">
              You may not be able to get it back.
            </Typography>
            <div className="w-full flex flex-row gap-2">
              <button type="button"
                className="p-2 rounded-md font-bold w-1/2 text-center bg-gray-200 hover:bg-gray-100 disabled:bg-gray-100 text-gray-900"
                disabled={releaseNumberProgress !== null}
                onClick={() => setConfirmingReleaseNumber(null)}
              >
                <Typography variant="mediumParagraph" fontWeight={600} color="#111827">Cancel</Typography>
              </button>
              <button type="button"
                className="p-2 rounded-md font-bold flex-grow flex flex-row items-center justify-center gap-2 bg-red-600 hover:bg-red-400 disabled:bg-red-400 text-white"
                disabled={releaseNumberProgress !== null}
                onClick={async () => {
                  setReleaseNumberProgress("Unregistering...")
                  const releasingNumber = confirmingReleaseNumber
                  const errorMessage = await getServicesManager().unregisterTwilioNumber(confirmingReleaseNumber)
                  setReleaseNumberProgress(errorMessage ?? "Unregistered!")
                  if (!errorMessage) { setSelectedNumbers(selectedNumbers.filter(x => x.rep_phone_value !== confirmingReleaseNumber)) }
                  await sleep(errorMessage ? 2000 : 1000)
                  if (confirmingReleaseNumber === releasingNumber) {
                    setConfirmingReleaseNumber(null)
                    setReleaseNumberProgress(null)
                  }
                }}
              >
                {releaseNumberProgress !== null && releaseNumberProgress.endsWith('...') ? <Loader className="mr-1 animate-spin" size={16} /> : null}
                <Typography variant="mediumParagraph" fontWeight={600} color="#ffffff">
                  {releaseNumberProgress ?? `Unregister ${format_number(confirmingReleaseNumber)}`}
                </Typography>
              </button>
            </div>
          </div>
        </div>}

      <NumberSearch 
        disabled={selectedNumbers.length >= maxSelections}
        handlePurchase={handlePurchase}
        purchasingNumbers={purchasingNumbers}
        quota={multiDialerQuota}
        registeredNumbers={selectedNumbers}
      />
    </div>
  )
}


